import React, { memo } from 'react';
import { compact, flatten, get } from 'lodash-es';
import { getFeaturesSpecsUrlStandard } from 'site-modules/shared/utils/core-link-constructor';
import { getFinalAssemblyCountry } from 'site-modules/shared/utils/features-specs/utils';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { getUsedVehiclePriceRange } from 'site-modules/shared/utils/core-page/pricing';
import { numberWithCommasForString } from 'site-modules/shared/utils/string';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import {
  COUNTRY_OF_FINAL_ASSEMBLY,
  HIGHLIGHTS_CONFIG,
  MSRP_LOWERCASE,
} from 'site-modules/personalized-search/configs/highlights-config';

const SUGGESTED_PRICE = 'Edmunds suggests you pay';
const ESTIMATED_PRICE = 'Estimated Price';
const AVERAGE_PRICE = 'Average price';
const STANDARD = 'Standard';

export function withHighlightsDataAdapter(WrappedComponent) {
  function HighlightsDataAdapter(props) {
    const {
      componentParams,
      vehicle,
      trimSummary,
      style,
      submodelAmounts,
      usedTmv,
      msrpPriceRanges,
      firstContent,
      averagePrice,
      ...rest
    } = props;

    const { labels } = componentParams;
    const styleId = trimSummary?.styleId;
    const makeSlug = vehicle?.make?.slug;
    const modelSlug = vehicle?.model?.slug;
    const year = vehicle?.year;
    const featuresSpecsUrl =
      styleId && makeSlug && modelSlug && getFeaturesSpecsUrlStandard({ makeSlug, modelSlug, year }, styleId);
    const assemblyCountry = getFinalAssemblyCountry(style);
    const features = style?.orderedFeatures?.length
      ? flatten(style.orderedFeatures.map(featureGroup => featureGroup.features))
      : [];

    const highlightsList =
      labels?.length && features?.length
        ? compact(
            labels.map(label => {
              const itemConfig = HIGHLIGHTS_CONFIG.find(item => label === item.name);

              if (itemConfig) {
                const { name, displayName, valuePath, getFormattedValue } = itemConfig;

                if (name === MSRP_LOWERCASE) {
                  let value = msrpPriceRanges;

                  if (vehicle && isUsed(vehicle.pubStates)) {
                    const { min, max } = getUsedVehiclePriceRange({
                      submodelAmounts,
                      usedTmv,
                      submodelSlug: vehicle.submodels?.slug,
                    });

                    value = { min, max };
                  }

                  return {
                    value: getFormattedValue(value),
                    name: displayName,
                  };
                }

                if (name === COUNTRY_OF_FINAL_ASSEMBLY) {
                  return assemblyCountry
                    ? {
                        value: assemblyCountry,
                        name: displayName,
                      }
                    : null;
                }

                if (valuePath) {
                  const value = get(props, valuePath);

                  return {
                    value: getFormattedValue ? getFormattedValue(value) : value,
                    name: displayName,
                  };
                }
              }

              const featureItem = features.find(feature => feature.name.toLowerCase() === label.toLowerCase());
              const displayName = itemConfig?.displayName || featureItem?.name;

              if (featureItem?.value) {
                return { ...featureItem, name: displayName, value: numberWithCommasForString(featureItem.value) };
              } else if (featureItem?.price) {
                return { ...featureItem, name: displayName, value: `+${getPriceString(featureItem?.price)}` };
              }

              return featureItem && { ...featureItem, name: displayName, value: STANDARD };
            })
          )
        : [];

    const suggestedPrice = trimSummary?.pricing?.suggestedPrice;
    const estimatedPrice = !!firstContent && get(firstContent, 'price.value');

    if (averagePrice) {
      highlightsList.unshift({
        name: AVERAGE_PRICE,
        value: getPriceString(averagePrice),
      });
    } else if (suggestedPrice) {
      highlightsList.unshift({
        name: SUGGESTED_PRICE,
        value: getPriceString(suggestedPrice),
      });
    } else if (estimatedPrice) {
      highlightsList.unshift({
        name: ESTIMATED_PRICE,
        value: estimatedPrice,
      });
    }

    return <WrappedComponent highlightsList={highlightsList} featuresSpecsUrl={featuresSpecsUrl} {...rest} />;
  }

  HighlightsDataAdapter.displayName = 'HighlightsDataAdapter';

  return memo(HighlightsDataAdapter);
}
